import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { exportStyledExcel, r2, MONEY_FMT } from '../../utils/exportExcel';
import { DOC_TYPES, DOC_TYPE_MAP, DOC_TYPE_GROUPS } from '../../utils/docTypes';
import Pagination from '../../components/Pagination';

const STATUS_COLORS = {
  draft:     'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const TYPE_COLORS = Object.fromEntries(
  Object.entries(DOC_TYPE_MAP).map(([k, v]) => [k, { bg: v.bg, color: v.color }])
);

// TypePickerModal — shown when user clicks "+ New Document"
function TypePickerModal({ onSelect, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <div className="font-bold text-gray-800 text-lg">مستند جديد</div>
            <div className="text-xs text-gray-400">اختر نوع المستند / Choose document type</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
        </div>
        <div className="p-4 space-y-4">
          {DOC_TYPE_GROUPS.map(g => (
            <div key={g.group}>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">{g.group}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {g.types.map(code => {
                  const t = DOC_TYPE_MAP[code];
                  return (
                    <button key={code} onClick={() => onSelect(code)}
                      className="flex items-start gap-3 p-3 rounded-xl border-2 border-gray-100 hover:border-emerald-400 hover:bg-emerald-50 transition-all text-left group">
                      <span className="text-2xl mt-0.5">{t.icon}</span>
                      <div>
                        <div className="font-bold text-gray-800 text-sm group-hover:text-emerald-700">{t.labelAr}</div>
                        <div className="text-xs text-gray-400">{t.labelEn}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{t.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const asArray = (d) =>
  Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];

export default function DocumentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom,     setDateFrom]     = useState('');
  const [dateTo,       setDateTo]       = useState('');
  const [search,       setSearch]       = useState('');
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  // Reset to page 1 whenever filters change
  const resetPage = () => setPage(1);

  const { data: docResult, isLoading } = useQuery({
    queryKey: ['documents', typeFilter, statusFilter, dateFrom, dateTo, search, page],
    queryFn: () => api.get('/api/documents', {
      params: {
        ...(typeFilter   && { type: typeFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(dateFrom     && { date_from: dateFrom }),
        ...(dateTo       && { date_to: dateTo }),
        ...(search       && { search }),
        page,
        limit: LIMIT,
      }
    }).then(r => r.data),
    keepPreviousData: true,
  });

  const documents  = docResult?.data  || [];
  const totalDocs  = docResult?.total || 0;
  const totalPages = docResult?.pages || 1;

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => api.get('/api/accounts', { params: { all: true } }).then(r => Array.isArray(r.data) ? r.data : r.data?.data || []),
  });

  const accountMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[String(a._id)] = a.name; });
    return m;
  }, [accounts]);

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/documents/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['documents']),
    onError: (err) => alert(err.response?.data?.error || t('error')),
  });

  // search is now server-side — documents already filtered
  const visibleDocs = documents;

  const fmtDocDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtTotal = (doc) => doc.currency === 'LBP'
    ? `${Math.round(doc.totals?.total || 0).toLocaleString('en-US')} LBP`
    : `$${(doc.totals?.total || 0).toFixed(2)}`;

  const handleExport = () => exportStyledExcel({
    filename: `documents_${dateFrom || 'all'}_${dateTo || 'today'}`,
    sheetName: 'Documents',
    title: 'Documents',
    subtitle: `${dateFrom || dateTo ? `Period: ${dateFrom || '…'} — ${dateTo || 'today'} · ` : ''}${visibleDocs.length} documents`,
    columns: [
      { header: 'Number', key: 'number', width: 14 },
      { header: 'Type', key: 'type', width: 16 },
      { header: 'Account', key: 'account', width: 24 },
      { header: 'Date', key: 'date', width: 13 },
      { header: 'Status', key: 'status', width: 11 },
      { header: 'Currency', key: 'currency', width: 10 },
      { header: 'Total', key: 'total', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Paid', key: 'paid', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Balance', key: 'balance', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Delivery', key: 'delivery', width: 10 },
    ],
    rows: visibleDocs.map(d => ({
      number: d.number || 'Draft',
      type: DOC_TYPES[d.type] || d.type,
      account: d.account_id ? (accountMap[String(d.account_id)] || '') : '',
      date: fmtDocDate(d.date),
      status: d.status,
      currency: d.currency || 'USD',
      total: r2(d.totals?.total),
      paid: r2(d.totals?.paid),
      balance: r2(d.totals?.balance),
      delivery: d.delivery?.enabled ? 'yes' : '',
    })),
  });

  const handlePrint = () => {
    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const rows = visibleDocs.map(d => `<tr><td>${esc(d.number || 'Draft')}</td><td>${esc(DOC_TYPES[d.type] || d.type)}</td><td>${esc(d.account_id ? (accountMap[String(d.account_id)] || '—') : '—')}</td><td>${fmtDocDate(d.date)}</td><td>${esc(d.status)}</td><td class="r">${esc(fmtTotal(d))}</td><td class="r">${(d.totals?.balance || 0).toFixed(2)}</td></tr>`).join('');
    const win = window.open('', '_blank');
    if (!win) { alert('Pop-up blocked — allow pop-ups to print.'); return; }
    win.document.write(`<html><head><title>Documents</title><style>body{font-family:Arial,sans-serif;font-size:12px;padding:24px;}h2{margin-bottom:4px;}p{color:#666;margin-top:0;}table{width:100%;border-collapse:collapse;margin-top:12px;}th,td{padding:6px 9px;border-bottom:1px solid #eee;text-align:left;}th{background:#f3f4f6;font-size:10px;text-transform:uppercase;}td.r,th.r{text-align:right;}</style></head><body><h2>Documents</h2><p>${dateFrom || dateTo ? `Period: ${dateFrom || '…'} — ${dateTo || 'today'} · ` : ''}${visibleDocs.length} documents</p><table><thead><tr><th>Number</th><th>Type</th><th>Account</th><th>Date</th><th>Status</th><th class="r">Total</th><th class="r">Balance</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  return (
    <div>
      {showTypePicker && (
        <TypePickerModal
          onSelect={(code) => { setShowTypePicker(false); navigate(`/documents/new?type=${code}`); }}
          onClose={() => setShowTypePicker(false)}
        />
      )}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-gray-800">{t('documents', 'Documents')}</h2>
        <div className="flex gap-2">
          <button onClick={handleExport}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg">
            Export Excel
          </button>
          <button onClick={handlePrint}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg">
            Print
          </button>
          <button
            onClick={() => setShowTypePicker(true)}
            className="text-white text-sm font-medium px-4 py-2 rounded-lg"
            style={{ background: '#2e9f86' }}
          >
            + مستند جديد / New
          </button>
        </div>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap items-center">
        <input type="text" placeholder="Search number or notes…" value={search}
          onChange={e => { setSearch(e.target.value); resetPage(); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" style={{ minWidth: 200 }} />
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); resetPage(); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="">All Types</option>
          {Object.values(DOC_TYPE_MAP).map(t => (
            <option key={t.code} value={t.code}>{t.icon} {t.labelAr} / {t.labelEn}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); resetPage(); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); resetPage(); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
        <span className="text-gray-400 text-sm">to</span>
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); resetPage(); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none" />
        {(search || typeFilter || statusFilter || dateFrom || dateTo) && (
          <button onClick={() => { setSearch(''); setTypeFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); resetPage(); }}
            className="text-sm text-red-500 hover:text-red-700 font-medium px-1">
            Clear
          </button>
        )}
        <span className="text-xs text-gray-400 ml-auto">{totalDocs.toLocaleString()} documents</span>
      </div>

      {/* mobile cards */}
      <div className="md:hidden space-y-2">
        {isLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">Loading…</div>
        ) : visibleDocs.length === 0 ? (
          documents.length === 0 && !typeFilter && !statusFilter && !dateFrom && !dateTo && !search ? (
            <DocEmptyState onNew={() => setShowTypePicker(true)} />
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">No invoices match your filters.</div>
          )
        ) : visibleDocs.map(doc => {
          const typeStyle = TYPE_COLORS[doc.type] || { bg: '#f3f4f6', color: '#374151' };
          const accountName = doc.account_id ? (accountMap[String(doc.account_id)] || '—') : '—';
          return (
            <div key={doc._id} onClick={() => navigate(`/documents/${doc._id}`)}
                 className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 active:bg-gray-50">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-gray-800 text-sm">{doc.number || <span className="text-gray-400 italic text-xs">Draft</span>}</span>
                <span className="font-bold text-gray-800 text-sm">{fmtTotal(doc)}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span style={{ background: typeStyle.bg, color: typeStyle.color, fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>{DOC_TYPES[doc.type] || doc.type}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium ${STATUS_COLORS[doc.status] || 'bg-gray-100 text-gray-600'}`}>{doc.status}</span>
                  {doc.delivery?.enabled && <span style={{ background: '#eff6ff', color: '#1e40af', fontSize: 10.5, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>🚚</span>}
                  {doc.source === 'pos' && <span style={{ background: '#f0fdf4', color: '#166534', fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>🖥 POS</span>}
                </div>
                <span className="text-xs text-gray-400">{fmtDocDate(doc.date)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">{accountName}</div>
            </div>
          );
        })}
      </div>

      {/* desktop table */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : visibleDocs.length === 0 ? (
          documents.length === 0 && !typeFilter && !statusFilter && !dateFrom && !dateTo && !search ? (
            <DocEmptyState onNew={() => setShowTypePicker(true)} />
          ) : (
            <div className="p-8 text-center text-gray-500">No invoices match your filters.</div>
          )
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Number</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Account</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Tags</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Total</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleDocs.map(doc => {
                const typeStyle = TYPE_COLORS[doc.type] || { bg: '#f3f4f6', color: '#374151' };
                const accountName = doc.account_id ? (accountMap[String(doc.account_id)] || '—') : '—';
                const isPos      = !!doc.pos_session_id || doc.source === 'pos';
                const isDelivery = !!doc.delivery?.enabled;
                const isPaid     = doc.status === 'confirmed' && (doc.totals?.balance || 0) <= 0 && (doc.totals?.paid || 0) > 0;
                const isPartial  = doc.status === 'confirmed' && (doc.totals?.balance || 0) > 0 && (doc.totals?.paid || 0) > 0;

                return (
                  <tr key={doc._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/documents/${doc._id}`)}>
                    <td className="px-4 py-3 font-semibold text-gray-800">
                      {doc.number || <span className="text-gray-400 italic text-xs">Draft</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span style={{ background: typeStyle.bg, color: typeStyle.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                        {DOC_TYPES[doc.type] || doc.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-sm">{accountName}</td>
                    <td className="px-4 py-3">
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {isPos && (
                          <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>
                            POS
                          </span>
                        )}
                        {isDelivery && (
                          <span style={{ background: '#eff6ff', color: '#1e40af', fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>
                            🚚 Delivery
                          </span>
                        )}
                        {isPaid && (
                          <span style={{ background: '#d1fae5', color: '#065f46', fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>
                            Paid
                          </span>
                        )}
                        {isPartial && (
                          <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20 }}>
                            Partial
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{fmtDocDate(doc.date)}</td>
                    <td className="px-4 py-3 font-semibold text-gray-800">{fmtTotal(doc)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[doc.status] || 'bg-gray-100 text-gray-600'}`}>
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <button onClick={() => navigate(`/documents/${doc._id}`)}
                          className="font-medium text-xs" style={{ color: '#2e9f86' }}>
                          {doc.status === 'draft' ? 'Edit' : 'View'}
                        </button>
                        {doc.status === 'draft' && (
                          <button onClick={() => { if (window.confirm('Delete this document?')) deleteMutation.mutate(doc._id); }}
                            className="text-red-500 hover:text-red-700 font-medium text-xs">
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <Pagination page={page} pages={totalPages} total={totalDocs} limit={LIMIT} onChange={setPage} />
      </div>
    </div>
  );
}

function DocEmptyState({ onNew }) {
  return (
    <div className="py-16 px-6 text-center">
      <div className="text-5xl mb-3">🧾</div>
      <div className="font-bold text-gray-700 text-base mb-2">ما في فواتير بعد / No invoices yet</div>
      <div className="text-gray-400 text-sm mb-5">Create your first document</div>
      <button
        onClick={onNew}
        className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg"
      >
        + New Document
      </button>
    </div>
  );
}
